import { Box, Button, Typography, Divider } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

import { RoleConfiguration } from "../../Create/components";
import { Action } from "./Action";
import { newRoleValidation } from "../../../../../libs/form-validations";
import { Alert } from "../../../../../shared";
import { useApiResponseParser } from "../../../../../hooks";

import { IRole } from "../../../../../interfaces";

interface RoleDetailsProps {
    role: IRole;
    onUpdate: (data: IRole) => Promise<void>;
    onDelete: () => Promise<void>;
    isUpdating: boolean;
    isDeleting: boolean;
    updateError?: unknown;
}

export function RoleDetails({
    role,
    onUpdate,
    onDelete,
    isUpdating,
    isDeleting,
    updateError,
}: RoleDetailsProps) {
    const navigate = useNavigate();
    const { parseErrorResponseData } = useApiResponseParser();

    // The form only manages editable fields (Name, Description, Permissions)
    // Id is preserved but not part of the validation schema
    const methods = useForm({
        resolver: newRoleValidation,
        defaultValues: {
            Name: role.Name || "",
            Description: role.Description || "",
        },
        mode: "onChange",
    });

    useEffect(() => {
        methods.reset({
            Name: role.Name,
            Description: role.Description || "",
        });
    }, [role, methods]);

    const onSubmit = methods.handleSubmit(async (data) => {
        // Merge the immutable Id back into the data for the API update
        await onUpdate({ ...data, Id: role.Id } as IRole);
    });

    return (
        <Box component="div" width="100%" p={3} overflow="auto">
            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <Box component="div" display="flex" flexDirection="column" maxWidth={700}>
                        <Box mb={2}>
                            <Typography level="title-lg">Role Details</Typography>
                            <Typography level="body-sm">Update the role information below.</Typography>
                        </Box>

                        <Box mb={2}>
                           <Typography level="body-xs" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                               Role Identifier
                           </Typography>
                           <Typography level="body-md" sx={{ mt: 0.5, p: 1, px: 1.5, bgcolor: 'neutral.softBg', borderRadius: 'sm', fontFamily: 'monospace', color: 'text.primary' }}>
                               {role.Id}
                           </Typography>
                        </Box>

                        <RoleConfiguration />

                        <Box display="flex" gap={2} justifyContent="flex-start" mt={4}>
                            <Button variant="outlined" color="neutral" onClick={() => navigate("/admin/roles")}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isUpdating || !methods.formState.isDirty}
                                loading={isUpdating}
                                loadingPosition="start"
                                variant={"soft"}
                                color="primary"
                            >
                                Update Role
                            </Button>
                        </Box>

                        <Alert
                            successMessage={
                                !isUpdating && methods.formState.isSubmitSuccessful && "Role updated successfully!"
                            }
                            errorMessage={updateError && parseErrorResponseData(updateError)}
                            sx={{ mt: 3 }}
                        />

                        <Action onDelete={onDelete} isDeleting={isDeleting} roleName={role.Name} />
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
}
